import "@/App.css";
import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import EmailLog from "./pages/EmailLog";
import MatchReview from "./pages/MatchReview";
import { IP_ROUTES } from "./inventory_planner/routes";
import Invoices from "./pages/Invoices";
import PriceFiles from "./pages/PriceFiles";
import UploadStatus from "./pages/UploadStatus";
import Reports from "./pages/Reports";
import InflationIndex from "./pages/InflationIndex";
import Benchmark from "./pages/Benchmark";
import InternalPriceComparison from "./pages/InternalPriceComparison";
import Settings from "./pages/Settings";
import Portal from "./pages/Portal";
import PortalAdmin from "./pages/PortalAdmin";
import RecordsUpdater from "./pages/RecordsUpdater";
import DataHub from "./pages/DataHub";
import SubmissionsHub from "./pages/SubmissionsHub";
import Landing from "./pages/Landing";
import SupplyScanner from "./pages/SupplyScanner";
import SupplyScannerTv from "./pages/SupplyScannerTv";
import Credits from "./pages/Credits";
import PriceModelling from "./pages/PriceModelling";
import BudgetModeller from "./pages/BudgetModeller";
import QbrScorecards from "./pages/QbrScorecards";
import SupplierProfile from "./pages/SupplierProfile";
import { MODULES, moduleForPath } from "./modules";
import Review from "./pages/Review";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { ReadFailureBoundary } from "./components/ReadFailureBoundary";

// Routes that should render WITHOUT the pricing app's navigation/chrome
// AND without auth gating. The supplier-facing portal lives at /portal and
// the email-link review screens at /review/:id are reached from emailed
// signed URLs with no session.
const STANDALONE_ROUTES = ["/portal", "/review", "/login", "/auth/callback"];

/**
 * Module-aware top navigation.
 *
 * The platform is organised as MODULES (see src/modules.js). At the platform
 * level — the home launcher (/) and Settings — the bar shows only the ProcOps
 * brand and the right-hand utility cluster (Settings · name · Sign out). Once
 * inside a module, the bar shows THAT module's tabs and nothing else, so
 * PriceGuard and Supply Scanner each get their own relevant tab set. Adding a
 * module is a registry change — no edits here.
 */
const ModuleSwitcher = ({ mod }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const dismiss = event => { if (event.key === "Escape") { event.preventDefault(); setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("keydown", dismiss);
    return () => document.removeEventListener("keydown", dismiss);
  }, [open]);
  return (
    <div style={{ position: "relative", marginLeft: 4 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-module-switcher"
        ref={triggerRef}
        aria-expanded={open}
        aria-controls="module-switcher-menu"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
          background: open ? "rgba(255,255,255,0.05)" : "none", border: "none",
          borderLeft: "1px solid var(--hairline)", borderRadius: 8,
          fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15,
          color: "var(--color-text-1)", padding: "6px 10px 6px 14px",
        }}
      >
        {mod.name}
        <span aria-hidden="true" style={{ fontSize: 10, color: "var(--color-text-3)", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s" }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            data-testid="nav-module-menu"
            id="module-switcher-menu"
            style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 50, width: "min(260px, calc(100vw - 32px))",
              background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid var(--hairline)",
              borderRadius: 12, padding: 6, boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
            }}
          >
            <div style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-4)", padding: "6px 10px 4px" }}>Switch module</div>
            {MODULES.map((m) => {
              const active = m.key === mod.key;
              return (
                <Link
                  key={m.key}
                  to={m.entry}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-module-${m.key}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 10px",
                    borderRadius: 8, textDecoration: "none",
                    background: active ? "rgba(236,72,153,0.12)" : "transparent",
                  }}
                >
                  <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#0c0818", background: m.accent }}>{m.mark}</span>
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--color-text-1)" }}>{m.name}</span>
                    <span style={{ fontSize: 11.5, color: "var(--color-text-3)" }}>{m.tagline}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const path = location.pathname;
  const mod = moduleForPath(path);
  const tabs = mod?.nav || [];
  const isActive = (item) => {
    if (item.path === mod?.entry) return path === item.path || (item.alias || []).includes(path);
    if (path === item.path || path.startsWith(item.path + "/")) return true;
    return (item.alias || []).some((a) => path === a || path.startsWith(a + "/"));
  };

  return (
    <nav className="nav-container">
      <div className="nav-content">
        <Link to="/" className="nav-brand" data-testid="nav-brand">
          <span className="nav-brand-mark" aria-hidden="true" />
          <span className="nav-title">ProcOps</span>
        </Link>
        {mod && <ModuleSwitcher mod={mod} />}
        <div className="nav-links">
          {tabs.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`nav-link ${isActive(item) ? "active" : ""}`}
              aria-current={isActive(item) ? "page" : undefined}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {user && (
          <div data-testid="user-menu" className="nav-user">
            <Link
              to="/settings"
              data-testid="nav-platform-settings"
              title="Settings"
              className={`nav-link ${path.startsWith("/settings") ? "active" : ""}`}
            >
              Settings
            </Link>
            <span data-testid="user-name" className="nav-user-name" title={user.email}>
              {user.name?.split(" ")[0] || user.email}
            </span>
            <button
              onClick={logout}
              data-testid="logout-btn"
              title="Sign out"
              className="nav-signout"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

const AppShell = () => {
  const location = useLocation();
  const isStandalone = STANDALONE_ROUTES.some(
    (r) =>
      location.pathname === r ||
      location.pathname.startsWith(r + "/") ||
      location.pathname.startsWith(r + "?"),
  );

  if (isStandalone) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/portal" element={<Portal />} />
        <Route path="/review/:submissionId" element={<Review />} />
      </Routes>
    );
  }

  // Full-bleed TV/kiosk dashboard — auth-gated but chrome-less (no nav,
  // no main-content padding) so it fills a 1080p screen exactly.
  if (location.pathname === "/supply-scanner/tv") {
    return (
      <ProtectedRoute>
        <Routes>
          <Route path="/supply-scanner/tv" element={<SupplyScannerTv />} />
        </Routes>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Navigation />
      <div className={`main-content ${location.pathname.startsWith('/supply-scanner') ? '' : 'audited-ui'}`}>
        <ReadFailureBoundary key={location.pathname + location.search} scope={location.pathname + location.search}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/supply-scanner" element={<SupplyScanner />} />
          {/* Merged "Data" hub — sub-tabs for Invoices / Price Files / Upload Status.
              Legacy routes are kept so existing links/tests still resolve. */}
          <Route path="/data" element={<DataHub />} />
          <Route path="/upload-status" element={<UploadStatus />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/price-files" element={<PriceFiles />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/credits" element={<Credits />} />
          <Route path="/email-log" element={<EmailLog />} />
          <Route path="/match-review" element={<MatchReview />} />
          {/* Inventory Planner — self-contained module; delete the folder + these lines to remove. */}
          {IP_ROUTES.map((r) => {
            const El = r.element;
            return <Route key={r.path} path={r.path} element={<El />} />;
          })}
          <Route path="/inflation-index" element={<InflationIndex />} />
          <Route path="/benchmark" element={<Benchmark />} />
          <Route path="/qbr" element={<PriceModelling />} />
          <Route path="/qbr/modelling" element={<PriceModelling />} />
          <Route path="/qbr/budget" element={<BudgetModeller />} />
          <Route path="/qbr/scorecards" element={<QbrScorecards />} />
          <Route path="/qbr/supplier/:name" element={<SupplierProfile />} />
          <Route path="/internal-price-comparison" element={<InternalPriceComparison />} />
          {/* Merged "Submissions" hub — Supplier Portal admin + Records Updater. */}
          <Route path="/submissions" element={<SubmissionsHub />} />
          <Route path="/portal-admin" element={<PortalAdmin />} />
          <Route path="/records-updater" element={<RecordsUpdater />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
        </ReadFailureBoundary>
      </div>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppShell />
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
